import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import SiteLink from 'components/shared/sitelink';
import Icon from 'components/shared/icon';
import Logo from 'components/shared/logo';
import { addLocaleToHref } from 'lib/utils/helpers';
import styles from './faqMobileNav.module.scss';

const DrawerItem = ({ item, locale, router }) => {
  const [expanded, setExpanded] = useState(item?.isActive || false);
  const hasChildren = item?.children?.length > 0;

  if (!hasChildren) {
    return (
      <SiteLink
        href={addLocaleToHref(item.href, locale, router)}
        locale={locale}
        external={item.external}
        reloaded={item.reloaded}
        className={classNames(styles.link, { [styles.active]: item.isActive })}
      >
        {item.label}
        <Icon type="chevron" className={styles.chevron} />
      </SiteLink>
    );
  }

  return (
    <>
      <button
        type="button"
        className={classNames(styles.link, {
          [styles.active]: item.isActive,
          [styles.expanded]: expanded,
        })}
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        {item.label}
        <Icon
          type="chevron"
          className={classNames(styles.chevron, { [styles.chevronExpanded]: expanded })}
        />
      </button>
      {expanded && (
        <ul className={styles.children}>
          <li className={styles.childItem}>
            <SiteLink
              href={addLocaleToHref(item.href, locale, router)}
              locale={locale}
              className={classNames(styles.childLink, { [styles.active]: item.isActive })}
            >
              {item.label}
            </SiteLink>
          </li>
          {item.children.map(child => (
            <li key={child.id} className={styles.childItem}>
              <SiteLink
                href={addLocaleToHref(child.href, locale, router)}
                locale={locale}
                className={classNames(styles.childLink, { [styles.active]: child.isActive })}
              >
                {child.label}
              </SiteLink>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

const FaqMobileNav = ({
  items,
  locale,
  hubTitle,
  chooseTopicLabel,
  closeLabel,
  className,
  id = 'faq-mobile-drawer',
}) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return undefined;
    const trigger = triggerRef.current;
    const drawer = drawerRef.current;
    const focusable = () =>
      drawer?.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || [];
    const onKey = e => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = focusable();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    focusable()[0]?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      if (trigger?.isConnected) trigger.focus();
    };
  }, [open]);

  useEffect(() => {
    const handleRouteChange = () => setOpen(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, [router.events]);

  if (!items?.length) return null;

  const drawer = (
    <div
      id={id}
      ref={drawerRef}
      className={classNames(styles.drawer, { [styles.drawerOpen]: open })}
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
      aria-label={hubTitle || chooseTopicLabel}
    >
      <div className={styles.drawerInner}>
        <div className={styles.logo}>
          <Logo className={styles.logoSvg} />
        </div>
        {hubTitle && <div className={styles.hubTitle}>{hubTitle}</div>}
        <ul className={styles.list}>
          {items.map(item => (
            <li key={item.id} className={styles.item}>
              <DrawerItem item={item} locale={locale} router={router} />
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        className={styles.closeButton}
        onClick={() => setOpen(false)}
        aria-label={closeLabel}
      >
        <Icon type="close" className={styles.closeIcon} />
      </button>
    </div>
  );

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={classNames(styles.trigger, className)}
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls={id}
      >
        {chooseTopicLabel}
      </button>
      {mounted && createPortal(drawer, document.body)}
    </>
  );
};

export default FaqMobileNav;
